import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, IconButton, Box, Stepper, Step, StepLabel, Tabs, Tab} from '@mui/material'
import {ArrowBack, CheckCircle} from '@mui/icons-material'
import {clientApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate, Link, useParams} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {parseError, ignoreTimezone} from '../../utils/helpers'
import {useMobile} from '../../hooks'
import {ServiceActivitiesAndNotes, ServiceAgreementDetails, ServiceAgreementDetailsSkeleton} from './components'
import dayjs from 'dayjs'
import {format} from 'date-fns'

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const [activeStep, setActiveStep] = useState(0)
    const [nextLabel, setNextLabel] = useState('Next')
    const navigate = useNavigate()
    const {mobile} = useMobile()
    const [serviceAgreementDetailsData, setServiceAgreementDetailsData] = useState(null)
    const [serviceActivitiesAndNotesData, setServiceActivitiesAndNotesData] = useState(null)
    const [oldServiceAgreementDetailsData, setOldServiceAgreementDetailsData] = useState(null)
    const [oldServiceActivitiesAndNotesData, setOldServiceActivitiesAndNotesData] = useState(null)
    const [updating, setUpdating] = useState(false)
    const {clientId, serviceAgreementId} = useParams()
    const [skeleton, setSkeleton] = useState(!!serviceAgreementId)
    const [lookupsSkeleton, setLookupsSkeleton] = useState(!!branchId)
    const detailsSkeleton = skeleton || lookupsSkeleton
    const steps = ['Service Agreement Details', 'Service Activities & Notes']
    const serviceAgreementDetailsForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                agreement_number: yup.string(),
                payer_id: yup.string(),
                status: yup.string().default('active'),
                diagnosis_code: yup.string().required('Diagnosis Code is required'),
                effective_date: yup.date().required('Effective Date is required'),
                through_date: yup.date().required('Through Date is required'),
                ma_mco_number: yup.string().required('MA/MCO Number is required'),
                service_items: yup.array().of(
                    yup.object().shape({
                        id: yup.string(),
                        branch_service_id: yup.string(),
                        start_date: yup
                            .date()
                            .nullable()
                            .transform((value, originalValue) => (originalValue === '' ? null : value)),
                        end_date: yup
                            .date()
                            .nullable()
                            .transform((value, originalValue) => (originalValue === '' ? null : value)),
                        total_units: yup
                            .number()
                            .nullable()
                            .transform((value, originalValue) => (originalValue === '' ? null : value)),
                        rate_per_unit: yup
                            .number()
                            .nullable()
                            .transform((value, originalValue) => (originalValue === '' ? null : value)),
                        status: yup.string(),
                        unique_id: yup.string(),
                    }),
                ),
            }),
        ),
    })
    const serviceActivitiesAndNotesForm = useForm({
        resolver: yupResolver(
            yup.object().shape({
                activities: yup.array().of(
                    yup.object().shape({
                        id: yup.string(),
                        service_item_id: yup.string(),
                        source_type: yup.string(),
                        activity_name: yup.string(),
                        instructions: yup.string(),
                        service_unique_id: yup.string(),
                    }),
                ),
            }),
        ),
    })

    useEffect(() => {
        ;(async () => {
            if (serviceAgreementId) {
                try {
                    setSkeleton(true)

                    const {
                        data: {data: data1},
                    } = await clientApi.getServiceAgreement(clientId, serviceAgreementId)
                    if (data1) {
                        setOldServiceAgreementDetailsData(data1)
                        setUpdating(!!data1)
                        serviceAgreementDetailsForm.setValue('agreement_number', data1?.agreement_number)
                        serviceAgreementDetailsForm.setValue('diagnosis_code', data1?.diagnosis_code)
                        serviceAgreementDetailsForm.setValue('effective_date', new Date(ignoreTimezone(data1?.effective_date)))
                        serviceAgreementDetailsForm.setValue('through_date', new Date(ignoreTimezone(data1?.through_date)))
                        serviceAgreementDetailsForm.setValue('ma_mco_number', data1?.ma_mco_number)
                        serviceAgreementDetailsForm.setValue('status', data1?.status)
                        const service_items = data1?.service_items?.map(item => ({
                            ...item,
                            start_date: dayjs(ignoreTimezone(item?.start_date)),
                            end_date: dayjs(ignoreTimezone(item?.end_date)),
                            unique_id: `${item?.branch_service_id}|${format(new Date(ignoreTimezone(item?.start_date)), 'yyyyMMdd')}|${format(new Date(ignoreTimezone(item?.end_date)), 'yyyyMMdd')}`,
                        }))
                        serviceAgreementDetailsForm.setValue('service_items', service_items)

                        let activities = []
                        for (const item of service_items ?? []) {
                            const {
                                data: {data: data2},
                            } = await clientApi.getServiceAgreementItemActivities(clientId, serviceAgreementId, item?.id)
                            activities = [
                                ...activities,
                                ...data2?.map(item2 => ({
                                    id: item2?.id,
                                    service_item_id: item?.id,
                                    source_type: 'custom',
                                    activity_name: item2?.activity_name,
                                    instructions: item2?.instructions,
                                    service_unique_id: item?.unique_id,
                                })),
                            ]
                        }
                        setOldServiceActivitiesAndNotesData({activities})
                        serviceActivitiesAndNotesForm.setValue('activities', activities)
                    }

                    setSkeleton(false)
                } catch (error) {
                    setSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                }
            }
        })()
    }, [])

    useEffect(() => {
        if (activeStep == 1) {
            setNextLabel('Submit')
        } else {
            setNextLabel('Next')
        }
    }, [activeStep])

    useEffect(() => {
        if (serviceActivitiesAndNotesData) {
            submit()
        }
    }, [serviceActivitiesAndNotesData])

    const previous = () => {
        setActiveStep(prev => --prev)
    }

    const next = () => {
        if (activeStep == 0) {
            serviceAgreementDetailsForm.handleSubmit(data => {
                setServiceAgreementDetailsData(data)
                setActiveStep(prev => ++prev)
            })()
        } else if (activeStep == 1) {
            serviceActivitiesAndNotesForm.handleSubmit(data => {
                setServiceActivitiesAndNotesData(data)
            })()
        }
    }

    const submit = async () => {
        if (!updating) {
            try {
                setLoading(true)

                const {
                    data: {data},
                } = await clientApi.addServiceAgreement(clientId, {
                    agreement_number: serviceAgreementDetailsData?.agreement_number,
                    payer_id: serviceAgreementDetailsData?.payer_id,
                    diagnosis_code: serviceAgreementDetailsData?.diagnosis_code,
                    effective_date: format(new Date(serviceAgreementDetailsData?.effective_date), 'yyyy-MM-dd'),
                    through_date: format(new Date(serviceAgreementDetailsData?.through_date), 'yyyy-MM-dd'),
                    ma_mco_number: serviceAgreementDetailsData?.ma_mco_number,
                    status: serviceAgreementDetailsData?.status,
                    service_items: serviceAgreementDetailsData?.service_items?.map(item => ({
                        ...item,
                        start_date: format(new Date(item?.start_date), 'yyyy-MM-dd'),
                        end_date: format(new Date(item?.end_date), 'yyyy-MM-dd'),
                    })),
                })

                const serviceItems = data?.service_items?.map(item => ({
                    ...item,
                    unique_id: `${item?.branch_service_id}|${format(new Date(ignoreTimezone(item?.start_date)), 'yyyyMMdd')}|${format(new Date(ignoreTimezone(item?.end_date)), 'yyyyMMdd')}`,
                }))
                for (const item of serviceActivitiesAndNotesData?.activities ?? []) {
                    const serviceItem = serviceItems.find(item2 => item2?.unique_id == item?.service_unique_id)
                    await clientApi.createServiceAgreementItemActivity(clientId, data?.id, serviceItem?.id, item)
                }

                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Service Agreement Added!',
                    description: `The service agreement has been successfully added and linked to the client's profile.`,
                })
                navigate(`/clients-management/${clientId}/service-agreement`)
            } catch (error) {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            }
        } else {
            try {
                setLoading(true)

                await clientApi.updateServiceAgreement(clientId, serviceAgreementId, {
                    diagnosis_code: serviceAgreementDetailsData?.diagnosis_code,
                    effective_date: format(new Date(serviceAgreementDetailsData?.effective_date), 'yyyy-MM-dd'),
                    through_date: format(new Date(serviceAgreementDetailsData?.through_date), 'yyyy-MM-dd'),
                    ma_mco_number: serviceAgreementDetailsData?.ma_mco_number,
                    status: serviceAgreementDetailsData?.status,
                })
                for (const item of serviceAgreementDetailsData?.service_items ?? []) {
                    const data = {
                        ...item,
                        start_date: format(new Date(item?.start_date), 'yyyy-MM-dd'),
                        end_date: format(new Date(item?.end_date), 'yyyy-MM-dd'),
                    }
                    if (item?.id) {
                        await clientApi.updateServiceAgreementItem(clientId, serviceAgreementId, item?.id, data)
                    } else {
                        await clientApi.addServiceAgreementItem(clientId, serviceAgreementId, data)
                    }
                }
                for (const oldItem of oldServiceAgreementDetailsData?.service_items ?? []) {
                    if (serviceAgreementDetailsData?.service_items?.findIndex(item => item?.id == oldItem?.id) == -1) {
                        await clientApi.deleteServiceAgreementItem(clientId, serviceAgreementId, oldItem?.id)
                    }
                }

                const {
                    data: {data},
                } = await clientApi.getServiceAgreement(clientId, serviceAgreementId)
                const serviceItems = data?.service_items?.map(item => ({
                    ...item,
                    unique_id: `${item?.branch_service_id}|${format(new Date(ignoreTimezone(item?.start_date)), 'yyyyMMdd')}|${format(new Date(ignoreTimezone(item?.end_date)), 'yyyyMMdd')}`,
                }))
                for (const item of serviceActivitiesAndNotesData?.activities ?? []) {
                    if (item?.id) {
                        await clientApi.updateServiceAgreementItemActivity(clientId, serviceAgreementId, item?.service_item_id, item?.id, item)
                    } else {
                        const serviceItem = serviceItems.find(item2 => item2?.unique_id == item?.service_unique_id)
                        await clientApi.createServiceAgreementItemActivity(
                            clientId,
                            serviceAgreementId,
                            item?.service_item_id ? item?.service_item_id : serviceItem?.id,
                            item,
                        )
                    }
                }
                for (const oldItem of oldServiceActivitiesAndNotesData?.activities ?? []) {
                    if (oldItem?.service_item_id && serviceActivitiesAndNotesData?.activities?.findIndex(item => item?.id == oldItem?.id) == -1) {
                        await clientApi.deleteServiceAgreementItemActivity(clientId, serviceAgreementId, oldItem?.service_item_id, oldItem?.id)
                    }
                }

                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Service Agreement Updated!',
                    description: `The service agreement has been successfully updated and linked to the client's profile.`,
                })
                navigate(`/clients-management/${clientId}/service-agreement`)
            } catch (error) {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            }
        }
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <Link to={`/clients-management/${clientId}/service-agreement`}>
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </Link>
                        <Typography variant="h6" color="text.primary">
                            Add Service Agreement
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" onClick={() => navigate(`/clients-management/${clientId}/service-agreement`)} style={{flex: 1}}>
                            Cancel
                        </Button>
                        {activeStep != 0 && (
                            <Button variant="outlined" onClick={previous} style={{flex: 1}}>
                                Previous
                            </Button>
                        )}
                        <Button variant="contained" onClick={next} disabled={detailsSkeleton} style={{flex: 1}}>
                            {nextLabel}
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        <Link className="link" to="/clients-management">
                            Clients Management
                        </Link>
                        {' / '}
                        <Link className="link" to={`/clients-management/${clientId}/service-agreement`}>
                            Service Agreement
                        </Link>{' '}
                        / Add Service Aggreement
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <Link className="back-button" to={`/clients-management/${clientId}/service-agreement`}>
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </Link>
                            <Typography variant="h5" color="text.primary">
                                Add Service Agreement
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button variant="outlined" onClick={() => navigate(`/clients-management/${clientId}/service-agreement`)}>
                                Cancel
                            </Button>
                            {activeStep != 0 && (
                                <Button variant="outlined" onClick={previous}>
                                    Previous
                                </Button>
                            )}
                            <Button variant="contained" onClick={next} disabled={detailsSkeleton}>
                                {nextLabel}
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            {mobile ? (
                <>
                    <Tabs value={activeStep} onChange={(e, newValue) => setActiveStep(newValue)} variant="scrollable">
                        <Tab label="Service Agreement Details" disabled />
                        <Tab label="Service Activities & Notes" disabled />
                    </Tabs>
                    <br />
                    <br />
                    {activeStep == 0 && detailsSkeleton && <ServiceAgreementDetailsSkeleton services={serviceAgreementId ? 1 : 0} />}
                    <div style={{display: activeStep != 0 || detailsSkeleton ? 'none' : 'block'}}>
                        <ServiceAgreementDetails form={serviceAgreementDetailsForm} updating={updating} setSkeleton={setLookupsSkeleton} />
                    </div>
                    <div style={{display: activeStep != 1 ? 'none' : 'block'}}>
                        <ServiceActivitiesAndNotes
                            form={serviceActivitiesAndNotesForm}
                            assignedServices={serviceAgreementDetailsData?.service_items}
                        />
                    </div>
                </>
            ) : (
                <div className="container">
                    <div className="step">
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={label} completed={index < activeStep}>
                                    <StepLabel
                                        slots={{
                                            stepIcon: ({active, completed, icon}) =>
                                                completed ? (
                                                    <CheckCircle color="primary" style={{fontSize: 28}} />
                                                ) : (
                                                    <Box
                                                        className="step-icon"
                                                        sx={theme => ({
                                                            backgroundColor: active ? theme.palette.primary.main : theme.palette.text.disabled,
                                                        })}>
                                                        {icon}
                                                    </Box>
                                                ),
                                        }}>
                                        <Typography variant="subtitle2">{label}</Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>
                    <div className="content">
                        {activeStep == 0 && detailsSkeleton && <ServiceAgreementDetailsSkeleton services={serviceAgreementId ? 1 : 0} />}
                        {activeStep == 0 && (
                            <div style={{display: detailsSkeleton ? 'none' : 'block'}}>
                                <ServiceAgreementDetails form={serviceAgreementDetailsForm} updating={updating} setSkeleton={setLookupsSkeleton} />
                            </div>
                        )}
                        {activeStep == 1 && (
                            <ServiceActivitiesAndNotes
                                form={serviceActivitiesAndNotesForm}
                                assignedServices={serviceAgreementDetailsData?.service_items}
                            />
                        )}
                    </div>
                </div>
            )}
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
